import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import cx from 'classnames';

import { excludeMileagePath, replaceHyphensWithSpace, transformLocationPath } from 'Utilities/breadcrumbs';
import { LOCATION_PAGES } from 'Utilities/locations';

import styles from './Breadcrumbs.scss';

type routeList = {
  label: string;
  route: string;
}[];

export type breadcrumbsProps = {
  readonly className?: string;
  readonly leftAlign?: boolean;
  readonly list?: routeList;
  readonly onYellow?: boolean;
};

const Breadcrumbs = ({
  className, leftAlign = false, list = [], onYellow = false,
}: breadcrumbsProps) => {
  const { pathname } = useLocation();

  const isLocationPage = LOCATION_PAGES.some((city) => pathname.includes(city));

  const links: routeList | [] = useMemo(() => {
    if (list.length) {
      return list;
    }

    const parts = transformLocationPath(excludeMileagePath(pathname));

    if (parts.length > 1) {
      let prevPath = '';
      const crumbs = parts.map((it: string) => {
        prevPath += `/${it}`;
        return {
          label: replaceHyphensWithSpace(it),
          route: prevPath,
        };
      });

      if (!isLocationPage) {
        crumbs.pop();
        crumbs.unshift({
          label: 'Home',
          route: '/',
        });
      }

      return crumbs;
    }

    return [];
  }, [list, pathname, isLocationPage]);

  return (
    <nav className={cx(styles.crumbs, className, {
      [styles.leftAlign]: leftAlign,
      [styles.hidden]: links.length === 0,
    })}
    >
      {
        links.map(({ label, route }, i) => (
          <React.Fragment key={label}>
            <Link className={cx(styles.link, {
              [styles.onYellow]: onYellow,
              [styles.disabled]: isLocationPage && (i === links.length - 1),
            })} to={(route === '/sell-my-car' || isLocationPage) ? '/' : route }>
              <span className={styles.inner}>{label}</span>
            </Link>
            {
              (links.length - 1 !== i) ? (
                <span className={styles.divider}>/</span>
              ) : (null)
            }
          </React.Fragment>
        ))
      }
    </nav>
  );
};

export default Breadcrumbs;
